import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useOffersUI from 'api/useOffersUI';
import { type FormikProps, type OfferUI } from 'common/contracts';
import { EFeatureFlag } from 'constants/enums';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import Input from 'components/Input/Input';
import InputSelectDropdown, {
  type InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import SegmentInput from 'components/SegmentInput/SegmentInput';
import { Separator } from 'components/ui/separator';

import { isMilestoneProgressBarDesign } from '../../ProgressBars.utils';

interface SettingsTabProps {
  formikProps: FormikProps;
  currentOfferUI?: string;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  currentOfferUI
}) => {
  const { publisherId } = useParams();
  const { handleChange, handleBlur, values, touched, errors, setFieldValue } =
    formikProps;
  const { getOffersUI } = useOffersUI(publisherId, currentOfferUI);
  const { getFeatureFlagByKey } = useFeatureFlags({ publisherId });
  const isMilestonesEnabled = Boolean(
    getFeatureFlagByKey(EFeatureFlag.REWARD_PROGRESS_BAR_MILESTONES_ENABLED)
  );

  const offerDesignOptions = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data?.result) return [];

    const getDesignTypeLabel = (offerUI: OfferUI): string | undefined => {
      if (!isMilestonesEnabled) return undefined;
      if (isMilestoneProgressBarDesign(offerUI)) return 'Milestone';
      return 'Standard';
    };

    return getOffersUI.data.result
      .filter((oi: OfferUI) => oi.offerUiType === OfferDesignTypes.ProgressBar)
      .map((offerUI: OfferUI) => ({
        label: offerUI.name,
        value: offerUI._id,
        externalId: offerUI.externalId,
        dataTestId: `progressBarDesignOption-${offerUI._id}`,
        secondaryLabel: getDesignTypeLabel(offerUI)
      }));
  }, [getOffersUI.isLoading, getOffersUI.data?.result, isMilestonesEnabled]);

  return (
    <div className="w-[528px] mx-auto">
      <RichText
        title="General"
        description="Start with setting up your bar mandatory fields"
        className="mb-5"
      />
      <div className="flex flex-col gap-5 mb-4">
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Bar External ID"
            dataTestId="progressBarExternalIdInput"
            placeholder="ID Number"
            type="text"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={
              touched.publisherOfferId && errors.publisherOfferId
                ? 'error'
                : 'default'
            }
            description={
              touched.publisherOfferId && errors.publisherOfferId
                ? errors.publisherOfferId
                : ''
            }
          />
          <Input
            label="Bar Name"
            dataTestId="progressBarNameInput"
            placeholder="Name"
            type="text"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={touched.name && errors.name ? 'error' : 'default'}
            description={touched.name && errors.name ? errors.name : ''}
          />
          <InputSelectDropdown
            label="Bar Offer Design"
            buttonDataTestId="progressBarDesignBox"
            listDataTestId="dashProgressBarDesignList"
            isRequired
            key={values.offerUiId}
            defaultSelected={offerDesignOptions.find(
              (option: InputSelectDropdownOption) =>
                option.value === values.offerUiId
            )}
            options={offerDesignOptions}
            onOptionChange={(newValue) => setFieldValue('offerUiId', newValue)}
            placeholder="Select"
          />
        </div>
      </div>
      <Separator className="my-9 bg-base-border" />
      <SegmentInput
        value={values.segments || []}
        onChange={(segments) => setFieldValue('segments', segments)}
        richTextDescription="Create or add players segments to your bar"
      />
      <Separator className="mt-5 mb-9 bg-base-border" />
    </div>
  );
};

export default SettingsTab;
